import { useRef, useState } from "react";
import { toast } from "sonner";

import { adminEndpoints, absoluteUrl, extractErrorAr } from "@/lib/adminApi";
import Icon from "@/components/Icon";

/**
 * Upload an image and store the returned server path in the parent form.
 * When `autoProcess` is true (products), we default-enable server-side
 * background removal + dark canvas composition, then let the admin choose
 * between the original and the processed version.
 */
export default function ImageField({
  value,
  onChange,
  label = "الصورة",
  testId = "image-field",
  autoProcess = false,
}) {
  const [uploading, setUploading] = useState(false);
  const [autoOn, setAutoOn] = useState(!!autoProcess);
  const [preview, setPreview] = useState(null); // { original_url, processed_url, process_mode, process_error }
  const inputRef = useRef(null);

  const pick = () => inputRef.current?.click();

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setPreview(null);
    try {
      const res = await adminEndpoints.upload(file, { process: autoOn });
      // Default: use the processed one if available, else the original
      onChange(res.url);
      if (res.processed) {
        setPreview(res);
        toast.success(
          res.process_mode === "full"
            ? "تمت المعالجة (إزالة الخلفية + توحيد التصميم)."
            : "تم توحيد قياس الصورة (وضع مخفف)."
        );
      } else if (res.process_error) {
        toast.error(`الصورة رُفعت لكن ${res.process_error}`);
      } else {
        toast.success("تم رفع الصورة.");
      }
    } catch (err) {
      toast.error(extractErrorAr(err));
    } finally {
      setUploading(false);
    }
  };

  const pickVariant = (url) => {
    onChange(url);
    toast.success("تم اختيار النسخة.");
  };

  const showThumb = value ? absoluteUrl(value) : null;

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>

      <div className="flex items-center gap-3">
        {showThumb ? (
          <img
            src={showThumb}
            alt=""
            className="w-20 h-20 rounded-xl object-cover border border-slate-200"
            data-testid={`${testId}-preview`}
          />
        ) : (
          <div className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-300">
            <Icon name="boxes" size={22} />
          </div>
        )}
        <div className="flex-1 min-w-0 space-y-2">
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="/api/static/... أو رابط خارجي"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-[#00E5FF]"
            dir="ltr"
            data-testid={`${testId}-input`}
          />
          <div className="flex gap-2 flex-wrap items-center">
            <button
              type="button"
              onClick={pick}
              disabled={uploading}
              className="text-xs font-semibold rounded-lg bg-[#0A1628] text-white px-3 py-1.5 hover:bg-[#0f2140] disabled:opacity-60 transition-colors"
              data-testid={`${testId}-upload`}
            >
              {uploading
                ? autoOn
                  ? "جاري المعالجة (قد تستغرق 10-20 ثانية)..."
                  : "جاري الرفع..."
                : "رفع صورة"}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setPreview(null);
                }}
                className="text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 px-3 py-1.5 hover:bg-slate-50"
                data-testid={`${testId}-clear`}
              >
                إزالة
              </button>
            )}
            {autoProcess && (
              <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={autoOn}
                  onChange={(e) => setAutoOn(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-slate-300 text-[#00b8cc] focus:ring-[#00E5FF]"
                  data-testid={`${testId}-autoprocess`}
                />
                معالجة تلقائية (إزالة الخلفية + خلفية داكنة موحّدة)
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Side-by-side preview after upload with processing */}
      {preview && preview.processed && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`${testId}-compare`}>
          <div className="rounded-xl border border-slate-100 p-3 bg-slate-50">
            <div className="text-xs font-semibold text-slate-600 mb-2">الأصلية</div>
            <img
              src={absoluteUrl(preview.original_url)}
              alt="original"
              className="w-full aspect-square object-contain rounded-lg bg-white"
            />
            <button
              type="button"
              onClick={() => pickVariant(preview.original_url)}
              disabled={value === preview.original_url}
              className="mt-2 w-full text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 py-1.5 hover:bg-white disabled:bg-cyan-50 disabled:border-[#00E5FF] disabled:text-[#00b8cc]"
              data-testid={`${testId}-use-original`}
            >
              {value === preview.original_url ? "المختارة حالياً" : "استخدم الأصلية"}
            </button>
          </div>
          <div className="rounded-xl border border-slate-100 p-3 bg-slate-50">
            <div className="text-xs font-semibold text-slate-600 mb-2">
              المعالجة{" "}
              {preview.process_mode === "lite" && (
                <span className="text-[10px] text-amber-600">(وضع مخفف — بدون إزالة خلفية)</span>
              )}
            </div>
            <img
              src={absoluteUrl(preview.processed_url)}
              alt="processed"
              className="w-full aspect-square object-contain rounded-lg bg-[#0A1628]"
            />
            <button
              type="button"
              onClick={() => pickVariant(preview.processed_url)}
              disabled={value === preview.processed_url}
              className="mt-2 w-full text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 py-1.5 hover:bg-white disabled:bg-cyan-50 disabled:border-[#00E5FF] disabled:text-[#00b8cc]"
              data-testid={`${testId}-use-processed`}
            >
              {value === preview.processed_url ? "المختارة حالياً" : "استخدم المعالجة"}
            </button>
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
    </div>
  );
}
